import { Box, Tooltip } from '@mui/material';
import { GridCellParams, GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { getMembers } from 'src/shared/services/memberService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { District } from 'src/shared/models/district/district';
import { Person, ManageAccounts } from '@mui/icons-material';
import { t } from 'i18next';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { MemberStatusEnum } from 'src/shared/models/enums/memberStatusEnum';
import { MemberSearchFilter } from '../models/memberSearchFilter';
import { MemberSearchFormState } from '../models/memberSearchFormState';
import { Member } from '../models/member';
import { MemberStatuses } from './memberStatuses';

type MemeberFamilyMemberDataGridProps = {
  searchFilter: MemberSearchFormState;
  pageSize: number;
  loadingCallback: (loading: boolean) => void;
  checkboxSelection: boolean;
  selectionModel: GridRowSelectionModel;
  setSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>;
  memberId: number;
  mainMemberLocalAssId: number;
};

export const MemberFamilyMemberDataGrid = (props: MemeberFamilyMemberDataGridProps) => {
  const { searchFilter, pageSize, loadingCallback, checkboxSelection, setSelectionModel, selectionModel, memberId, mainMemberLocalAssId } = props;
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'lastName', sort: 'asc' }]);

  const [, setAllRows] = useState<Member[]>([]); // Spar undan en array med data för alla sidor som hämtats för att kunna filtrera selected mot denna
  const [rows, setRows] = useState<Member[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [10, 25, 50, 100],
    pageSize: pageSize || 25,
    page: 0,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
  };

  const uniqueById = (items: Array<Member>) => {
    const set = new Set();
    return items.filter((item) => {
      const isDuplicate = set.has(item.member_id);
      set.add(item.member_id);
      return !isDuplicate;
    });
  };

  useEffect(() => {
    ConsoleHelper.log('searchFilter in MemberDataGrid', searchFilter);
    const fetchData = async () => {
      if (isMounted()) {
        updateData('loading', true);
        loadingCallback(true);
      }

      const searchString = searchFilter.searchString ? searchFilter.searchString.trim() : '';

      const associationIds = Array.isArray(searchFilter.districtIds)
        ? (searchFilter.districtIds as District[])
            .filter((x) => !x.isParent)
            .map((x) => x.code)
            .map(Number)
        : [];

      const searchParams: MemberSearchFilter = {
        associationId: mainMemberLocalAssId,
        associationIds,
        searchString,
        status: [MemberStatusEnum.Paid, MemberStatusEnum.NotPaid, MemberStatusEnum.NewMember],
        hasFamily: 'false',
        excludeMemberId: memberId,
      };

      try {
        const result = await getMembers(data.page, data.pageSize, sortModel[0].field, sortModel[0].sort, searchParams, [], '');
        // setSelectionModel(() => memberResult.map((r) => r.id));

        if (isMounted()) {
          updateData('totalRows', result.data.totalItems);
          const memberResult = extendObjectWithId(result.data.results, 'member_id');
          setTimeout(() => {
            setRows(memberResult);
            setAllRows((prev) => {
              return uniqueById([...prev, ...memberResult]);
            });
          }, 100);
        }
      } catch (error) {
        if (isMounted()) {
          updateData('loading', false);
          ConsoleHelper.log(error ?? 'Unkown error: getNewsListPageDetails');
        }
      } finally {
        if (isMounted()) {
          updateData('loading', false);
          loadingCallback(false);
        }
      }
    };
    fetchData();
  }, [loadingCallback, data.page, data.pageSize, sortModel, searchFilter, isMounted, memberId, mainMemberLocalAssId]);

  const columns: GridColDef[] = [
    { field: 'memberNr', headerName: t('member:tableHeaders.member_id'), flex: 0.5 },
    {
      field: 'firstName',
      headerName: t('member:tableHeaders.name'),
      sortable: true,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        const { isFamilyMember, isMainMember } = params.row;
        return (
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
            <span>{`${params.row.firstName || ''} ${params.row.lastName}`}</span>
            {isFamilyMember && (
              <Tooltip title={t('member:memberDetails.familyMember')} arrow>
                <Person fontSize='small' />
              </Tooltip>
            )}
            {isMainMember && (
              <Tooltip title={t('member:memberDetails.headMember')} arrow>
                <ManageAccounts fontSize='small' />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'lastName',
      headerName: t('member:tableHeaders.address'),
      sortable: false,
      flex: 1.25,
      renderCell: (params: GridCellParams) => {
        return <>{`${params.row.address && `${params.row.address}, `}${params.row.postNr} ${params.row.postAddress}`}</>;
      },
    },
    // { field: 'postAddress', headerName: t('member:tableHeaders.postAddress'), width: 140 },
    {
      field: 'association',
      headerName: getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.LocalLabel, t('member:tableHeaders.association')),
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        return params.row.association?.localAss && <div className='rowitem'>{params.row.association?.localAss}</div>;
      },
    },
    {
      field: 'district',
      headerName: getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.CountyLabel, t('member:tableHeaders.district')),
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        return params.row.district?.countyAss && <div className='rowitem'>{params.row.district?.countyAss}</div>;
      },
    },
    {
      field: 'status',
      headerName: t('member:tableHeaders.memberStatus'),
      sortable: false,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        const { status: stat, resDate } = params.row;
        return <MemberStatuses status={stat} resDate={resDate} chipSize='small' />;
      },
    },
  ];

  return (
    <Box>
      <DataGrid<Member>
        autoHeight
        columns={columns}
        rows={rows}
        dataModel={data}
        disableSelectionOnClick
        checkboxSelection={checkboxSelection}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
        // onRowClick={onRowClick}
        selectionModel={selectionModel}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </Box>
  );
};
